import { useCallback, useState } from 'react';
import CreateViewModal from 'framework-listing-lib/internal/ViewTabs/components/CreateViewModal';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import ViewShepherd from 'framework-listing-lib/internal/ViewTabs/components/ViewShepherd';
import SaveViewButton from 'framework-listing-lib/internal/ViewTabs/components/SaveViewButton';
import useCrmObjectTypeListingSort from 'framework-listing-lib/internal/hooks/useCrmObjectTypeListingSort';
import useCrmObjectTypeListingFilters from 'framework-listing-lib/hooks/useCrmObjectTypeListingFilters';
import useMutateViews from 'framework-listing-lib/internal/ViewTabs/hooks/useMutateViews';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import useCurrentView, { useIsCurrentViewReadOnly } from 'framework-listing-lib/hooks/useCurrentView';
import { useOnViewIdChange, useViewsNamespace } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { getViewId } from 'framework-listing-lib/internal/ViewTabs/utils/views';
import useViewForm from '../hooks/useViewForm';
import { jsx as _jsx } from "react/jsx-runtime";
export default function CrmListingSaveViewButton() {
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const currentView = useCurrentView();
  const namespace = useViewsNamespace();
  const onViewIdChange = useOnViewIdChange();
  const {
    onSetSort
  } = useCrmObjectTypeListingSort();
  const {
    onClearFilters
  } = useCrmObjectTypeListingFilters();
  const objectType = useObjectTypeId();
  const {
    updateView,
    loading
  } = useMutateViews();
  const [state, setState] = useState({
    showCreateModal: false,
    showViewPopover: false,
    formType: 'CREATE_NEW'
  });
  const buildViewRequest = useViewForm();
  const isReadOnly = useIsCurrentViewReadOnly();
  const handleViewSaved = useCallback(viewId => {
    setState(prevState => Object.assign({}, prevState, {
      showCreateModal: false
    }));
    onViewIdChange(viewId);
    trackCrmTableInteraction({
      action: 'change-view',
      viewId
    });
  }, [onViewIdChange, trackCrmTableInteraction]);
  const handleSaveViewClick = useCallback(() => {
    if (currentView && isReadOnly) {
      trackCrmTableInteraction({
        action: 'show-read-only-popover',
        viewId: getViewId(currentView)
      });
    }
    setState(prevState => Object.assign({}, prevState, {
      showViewPopover: true
    }));
  }, [currentView, isReadOnly, trackCrmTableInteraction]);
  const handleSaveAsNewClick = useCallback(() => {
    trackCrmTableInteraction({
      action: 'click-save-as-new',
      isDefaultView: true
    });
    setState(prevState => Object.assign({}, prevState, {
      formType: 'CLONE_WITH_CHANGES',
      showCreateModal: true,
      showViewPopover: false
    }));
  }, [trackCrmTableInteraction]);
  const handleUpdateView = useCallback(() => {
    if (loading) {
      return;
    }
    const viewRequest = buildViewRequest('UPDATE');
    updateView(viewRequest).then(updatedView => {
      if (!updatedView) {
        return;
      }
      setState(prevState => Object.assign({}, prevState, {
        showViewPopover: false
      }));
    }).catch(() => {
      // Error is being handled by useMutateViews
    });
  }, [loading, updateView, buildViewRequest]);
  const handleResetClick = useCallback(() => {
    if (!currentView) {
      return;
    }
    onClearFilters();
    onSetSort([{
      id: currentView.state.sortKey,
      desc: currentView.state.order === 1
    }]);
    setState(prevState => Object.assign({}, prevState, {
      showViewPopover: false
    }));
  }, [currentView, onSetSort, onClearFilters]);
  if (state.showCreateModal) {
    return /*#__PURE__*/_jsx(CreateViewModal, {
      formType: state.formType,
      namespace: namespace,
      objectTypeId: objectType,
      onClose: () => setState(prevState => Object.assign({}, prevState, {
        showCreateModal: false
      })),
      onCreate: handleViewSaved
    });
  }
  const saveViewButton = /*#__PURE__*/_jsx(SaveViewButton, {
    onClick: handleSaveViewClick,
    size: 'small'
  });
  if (state.showViewPopover) {
    return /*#__PURE__*/_jsx(ViewShepherd, {
      onOpenChange: event => setState(prevState => Object.assign({}, prevState, {
        showViewPopover: event.target.value
      })),
      onResetClick: handleResetClick,
      onSaveAsNewClick: handleSaveAsNewClick,
      onSaveViewClick: handleUpdateView,
      open: state.showViewPopover,
      children: saveViewButton
    });
  }
  if (!currentView) {
    return null;
  }
  return saveViewButton;
}